import React, { useContext, useEffect } from 'react'
import { Form, Input } from 'antd'
import { QuestionnaireItem } from '@medplum/fhirtypes'
import { QuestionnaireAnswerType } from 'components/Assessments/types'
import QuestionContainer from 'components/Assessments/components/QuestionContainer'
import LabelAndDescriptionFieldLayout from 'components/Assessments/components/FieldLayout/LabelAndDescriptionFieldLayout'
import AssessmentFormContext from 'contexts/AssessmentFormContext'
import { ScoreAndSummaryFormField } from './formField'
import { ScoreAndSummaryQuestionnaireItem } from './questionnaire'
import { AssessmentSummary } from './components/AssessmentSummary'
import { useGenerateAnswerFromSectionResults } from '../../hooks/useGenerateAnswerFromSectionResults'
import { calculateTotalAssessmentValue, generateScoreAndDurationString } from '../../helpers'
import { SectionDependentItems } from '../../types/questionnaire'
import { PATSectionComponentProps } from '../../types/components'
import { parseHelperTexts } from '../../components/utils'
import { UNANSWERED_DECIMAL_DEFAULT, UNANSWERED_SUMMARY_INPUT_DEFAULT } from '../../constants'

function escapeGreaterThanAtStart(text: string): string {
  if (text.trimStart().startsWith('>')) {
    const trimmedStart = text.trimStart()
    const leadingWhitespace = text.slice(0, text.length - trimmedStart.length)
    return `${leadingWhitespace}${trimmedStart.replace(/^>/, '&gt;')}`
  }
  return text
}

export default function ScoreAndSummarySection({
  item: section,
  sectionId: _sectionId,
  sectionIndex: _sectionIndex
}: PATSectionComponentProps): JSX.Element {
  const { form, triggerAutosave } = useContext(AssessmentFormContext)

  // Extract fields from section
  const modifierAField = section?.item?.find(
    (item) => item.linkId === ScoreAndSummaryQuestionnaireItem.SCORE_AND_SUMMARY_MODIFIER_A
  )
  const modifierBField = section?.item?.find(
    (item) => item.linkId === ScoreAndSummaryQuestionnaireItem.SCORE_AND_SUMMARY_MODIFIER_B
  )
  const cnaSkilledTasksField = section?.item?.find(
    (item) => item.linkId === ScoreAndSummaryQuestionnaireItem.SCORE_AND_SUMMARY_CNA_SKILLED_TASKS
  )
  const totalField = section?.item?.find(
    (item) => item.linkId === ScoreAndSummaryQuestionnaireItem.SCORE_AND_SUMMARY_TOTAL
  )

  // Get calculated values from other sections
  const { generatedItemValue: modifierAValue } = useGenerateAnswerFromSectionResults({
    questionType: QuestionnaireAnswerType.DECIMAL,
    questionLinkId: SectionDependentItems.MODIFIER_A,
    questionText: modifierAField?.text
  })

  const { generatedItemValue: modifierBValue } = useGenerateAnswerFromSectionResults({
    questionType: QuestionnaireAnswerType.DECIMAL,
    questionLinkId: SectionDependentItems.MODIFIER_B,
    questionText: modifierBField?.text
  })

  const { generatedItemValue: cnaSkilledTasksValue } = useGenerateAnswerFromSectionResults({
    questionType: QuestionnaireAnswerType.DECIMAL,
    questionLinkId: SectionDependentItems.CNA_SKILLED_TASKS,
    questionText: cnaSkilledTasksField?.text
  })

  // Watch form values to get current calculated values
  const modifierAFormValue = Form.useWatch(
    ScoreAndSummaryFormField.SCORE_AND_SUMMARY_MODIFIER_A,
    form
  )
  const modifierBFormValue = Form.useWatch(
    ScoreAndSummaryFormField.SCORE_AND_SUMMARY_MODIFIER_B,
    form
  )
  const cnaSkilledTasksFormValue = Form.useWatch(
    ScoreAndSummaryFormField.SCORE_AND_SUMMARY_CNA_SKILLED_TASKS,
    form
  )
  const totalFormValue = Form.useWatch(ScoreAndSummaryFormField.SCORE_AND_SUMMARY_TOTAL, form)

  // Calculate total assessment value explicitly from form values
  const totalAssessmentValue = React.useMemo(() => {
    const formValuesForCalculation = {
      [ScoreAndSummaryFormField.SCORE_AND_SUMMARY_MODIFIER_A]: modifierAFormValue,
      [ScoreAndSummaryFormField.SCORE_AND_SUMMARY_MODIFIER_B]: modifierBFormValue,
      [ScoreAndSummaryFormField.SCORE_AND_SUMMARY_CNA_SKILLED_TASKS]: cnaSkilledTasksFormValue
    }
    return calculateTotalAssessmentValue(formValuesForCalculation)
  }, [modifierAFormValue, modifierBFormValue, cnaSkilledTasksFormValue])

  // Extract numeric values from generatedItemValue
  const extractNumericValue = (value: unknown): number | undefined => {
    if (typeof value === 'number') {
      return Number.isNaN(value) ? undefined : value
    }
    if (value && typeof value === 'object' && 'value' in value) {
      const val = (value as { value: unknown }).value
      if (typeof val === 'number') {
        return Number.isNaN(val) ? undefined : val
      }
    }
    return undefined
  }

  // Update form values when calculated values change
  useEffect(() => {
    if (!form || !triggerAutosave) return

    const updates: Record<string, number> = {}

    const fieldUpdates = [
      {
        value: modifierAValue,
        formValue: modifierAFormValue,
        fieldName: ScoreAndSummaryFormField.SCORE_AND_SUMMARY_MODIFIER_A
      },
      {
        value: modifierBValue,
        formValue: modifierBFormValue,
        fieldName: ScoreAndSummaryFormField.SCORE_AND_SUMMARY_MODIFIER_B
      },
      {
        value: cnaSkilledTasksValue,
        formValue: cnaSkilledTasksFormValue,
        fieldName: ScoreAndSummaryFormField.SCORE_AND_SUMMARY_CNA_SKILLED_TASKS
      },
      {
        value: totalAssessmentValue,
        formValue: totalFormValue,
        fieldName: ScoreAndSummaryFormField.SCORE_AND_SUMMARY_TOTAL
      }
    ]

    fieldUpdates.forEach(({ value, formValue, fieldName }) => {
      const numericValue = extractNumericValue(value)
      if (numericValue !== undefined && formValue !== numericValue) {
        updates[fieldName] = numericValue
      }
    })

    if (Object.keys(updates).length > 0) {
      // Apply all updates at once using setFieldsValue and trigger autosave
      form.setFieldsValue(updates)
      triggerAutosave()
    }
  }, [
    form,
    triggerAutosave,
    modifierAValue,
    modifierBValue,
    cnaSkilledTasksValue,
    totalAssessmentValue,
    modifierAFormValue,
    modifierBFormValue,
    cnaSkilledTasksFormValue,
    totalFormValue
  ])

  // Helper function to get display value for a field
  const getDisplayValue = (linkId: string, formValue?: number): string => {
    const answerValue = formValue ?? UNANSWERED_DECIMAL_DEFAULT
    const sectionName = linkId.split('/')[1]
    return (
      generateScoreAndDurationString(answerValue, sectionName) || UNANSWERED_SUMMARY_INPUT_DEFAULT
    )
  }

  // Helper function to create getValueProps for formatting numeric values to display strings
  const createGetValueProps = (linkId: string) => (value: number | undefined) => {
    const displayValue = getDisplayValue(linkId, value)
    return { value: displayValue }
  }

  const getSecondaryText = (field: QuestionnaireItem | undefined): string | undefined => {
    if (!field) return undefined
    const helperTexts = parseHelperTexts(field)
    if (helperTexts.length === 0) return undefined

    const escapedTexts = helperTexts.map(escapeGreaterThanAtStart)
    return escapedTexts.join('  \n')
  }

  if (!section || !section.item || !Array.isArray(section.item)) {
    return <></>
  }

  return (
    <>
      <AssessmentSummary />

      {modifierAField && (
        <QuestionContainer linkId={ScoreAndSummaryQuestionnaireItem.SCORE_AND_SUMMARY_MODIFIER_A}>
          <LabelAndDescriptionFieldLayout
            label={modifierAField.text}
            secondaryText={getSecondaryText(modifierAField)}
          >
            <Form.Item
              name={ScoreAndSummaryFormField.SCORE_AND_SUMMARY_MODIFIER_A}
              getValueProps={createGetValueProps(
                ScoreAndSummaryQuestionnaireItem.SCORE_AND_SUMMARY_MODIFIER_A
              )}
              style={{ margin: 0 }}
            >
              <Input disabled />
            </Form.Item>
          </LabelAndDescriptionFieldLayout>
        </QuestionContainer>
      )}

      {modifierBField && (
        <QuestionContainer linkId={ScoreAndSummaryQuestionnaireItem.SCORE_AND_SUMMARY_MODIFIER_B}>
          <LabelAndDescriptionFieldLayout
            label={modifierBField.text}
            secondaryText={getSecondaryText(modifierBField)}
          >
            <Form.Item
              name={ScoreAndSummaryFormField.SCORE_AND_SUMMARY_MODIFIER_B}
              getValueProps={createGetValueProps(
                ScoreAndSummaryQuestionnaireItem.SCORE_AND_SUMMARY_MODIFIER_B
              )}
              style={{ margin: 0 }}
            >
              <Input disabled />
            </Form.Item>
          </LabelAndDescriptionFieldLayout>
        </QuestionContainer>
      )}

      {cnaSkilledTasksField && (
        <QuestionContainer
          linkId={ScoreAndSummaryQuestionnaireItem.SCORE_AND_SUMMARY_CNA_SKILLED_TASKS}
        >
          <LabelAndDescriptionFieldLayout
            label={cnaSkilledTasksField.text}
            secondaryText={getSecondaryText(cnaSkilledTasksField)}
          >
            <Form.Item
              name={ScoreAndSummaryFormField.SCORE_AND_SUMMARY_CNA_SKILLED_TASKS}
              getValueProps={createGetValueProps(
                ScoreAndSummaryQuestionnaireItem.SCORE_AND_SUMMARY_CNA_SKILLED_TASKS
              )}
              style={{ margin: 0 }}
            >
              <Input disabled />
            </Form.Item>
          </LabelAndDescriptionFieldLayout>
        </QuestionContainer>
      )}

      {totalField && (
        <QuestionContainer linkId={ScoreAndSummaryQuestionnaireItem.SCORE_AND_SUMMARY_TOTAL}>
          <LabelAndDescriptionFieldLayout
            label={totalField.text}
            secondaryText={getSecondaryText(totalField)}
          >
            <Form.Item
              name={ScoreAndSummaryFormField.SCORE_AND_SUMMARY_TOTAL}
              getValueProps={createGetValueProps(
                ScoreAndSummaryQuestionnaireItem.SCORE_AND_SUMMARY_TOTAL
              )}
              style={{ margin: 0 }}
            >
              <Input disabled />
            </Form.Item>
          </LabelAndDescriptionFieldLayout>
        </QuestionContainer>
      )}
    </>
  )
}
